export type AnimeListSortField =
  | 'id'
  | 'created_at'
  | 'updated_at'
  | 'view_count'
  | 'views_yesterday'
  | 'views_7d'
  | 'views_30d'
export type AnimeListSortOrder = 'asc' | 'desc'

export interface AnimeListFilters {
  search: string
  is_published?: boolean
  nsfw?: boolean
  is_recommended?: boolean
  type_id?: number
  series_id?: number
}

export interface AnimeListState {
  page: number
  filters: AnimeListFilters
  sortBy: {
    field: AnimeListSortField
    order: AnimeListSortOrder
  }
}

export type AnimeListSearch = Partial<{
  q: string
  published: '0' | '1'
  nsfw: '0' | '1'
  recommended: '0' | '1'
  type_id: number
  series_id: number
  sort: AnimeListSortField
  order: AnimeListSortOrder
  page: number
}>

export const ANIME_LIST_STATE_STORAGE_KEY = 'xfdm.admin.animeListState.v1'

export const DEFAULT_ANIME_LIST_STATE: AnimeListState = {
  page: 1,
  filters: { search: '' },
  sortBy: { field: 'updated_at', order: 'desc' },
}

const SEARCH_KEYS = new Set([
  'q',
  'published',
  'nsfw',
  'recommended',
  'type_id',
  'series_id',
  'sort',
  'order',
  'page',
])

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {}
}

function parseBoolean(value: unknown): boolean | undefined {
  if (value === true || value === '1') return true
  if (value === false || value === '0') return false
  return undefined
}

function parsePositiveInt(value: unknown): number | undefined {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : Number.NaN
  return Number.isInteger(n) && n > 0 ? n : undefined
}

function parseSortField(value: unknown): AnimeListSortField | undefined {
  return value === 'id' ||
    value === 'created_at' ||
    value === 'updated_at' ||
    value === 'view_count' ||
    value === 'views_yesterday' ||
    value === 'views_7d' ||
    value === 'views_30d'
    ? value
    : undefined
}

function parseSortOrder(value: unknown): AnimeListSortOrder | undefined {
  return value === 'asc' || value === 'desc' ? value : undefined
}

function parseSearchText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeState(raw: unknown): AnimeListState {
  const record = asRecord(raw)
  const filtersRecord = asRecord(record.filters ?? record)

  const filters: AnimeListFilters = {
    search: parseSearchText(filtersRecord.search ?? record.q),
  }
  const published = parseBoolean(filtersRecord.is_published ?? record.published)
  const nsfw = parseBoolean(filtersRecord.nsfw ?? record.nsfw)
  const recommended = parseBoolean(
    filtersRecord.is_recommended ?? record.recommended,
  )
  const typeId = parsePositiveInt(filtersRecord.type_id ?? record.type_id)
  const seriesId = parsePositiveInt(filtersRecord.series_id ?? record.series_id)

  if (published !== undefined) filters.is_published = published
  if (nsfw !== undefined) filters.nsfw = nsfw
  if (recommended !== undefined) filters.is_recommended = recommended
  if (typeId !== undefined) filters.type_id = typeId
  if (seriesId !== undefined) filters.series_id = seriesId

  const sortRecord = asRecord(record.sortBy)
  const field =
    parseSortField(sortRecord.field) ??
    parseSortField(record.sort) ??
    DEFAULT_ANIME_LIST_STATE.sortBy.field
  const order =
    parseSortOrder(sortRecord.order) ??
    parseSortOrder(record.order) ??
    DEFAULT_ANIME_LIST_STATE.sortBy.order

  return {
    page: parsePositiveInt(record.page) ?? DEFAULT_ANIME_LIST_STATE.page,
    filters,
    sortBy: { field, order },
  }
}

export function hasAnimeListSearch(raw: unknown): boolean {
  return Object.keys(asRecord(raw)).some((key) => SEARCH_KEYS.has(key))
}

export function parseAnimeListSearch(raw: unknown): AnimeListState {
  return normalizeState(raw)
}

export function validateAnimeListSearch(raw: unknown): AnimeListSearch {
  return serializeAnimeListSearch(parseAnimeListSearch(raw))
}

export function serializeAnimeListSearch(
  state: AnimeListState,
): AnimeListSearch {
  const normalized = normalizeState(state)
  const search: AnimeListSearch = {}
  const filters = normalized.filters

  if (filters.search.trim()) search.q = filters.search.trim()
  if (filters.is_published !== undefined) {
    search.published = filters.is_published ? '1' : '0'
  }
  if (filters.nsfw !== undefined) search.nsfw = filters.nsfw ? '1' : '0'
  if (filters.is_recommended !== undefined) {
    search.recommended = filters.is_recommended ? '1' : '0'
  }
  if (filters.type_id !== undefined) search.type_id = filters.type_id
  if (filters.series_id !== undefined) search.series_id = filters.series_id

  if (
    normalized.sortBy.field !== DEFAULT_ANIME_LIST_STATE.sortBy.field ||
    normalized.sortBy.order !== DEFAULT_ANIME_LIST_STATE.sortBy.order
  ) {
    search.sort = normalized.sortBy.field
    search.order = normalized.sortBy.order
  }

  if (normalized.page !== DEFAULT_ANIME_LIST_STATE.page) {
    search.page = normalized.page
  }

  return search
}

export function toStoredAnimeListState(state: AnimeListState): AnimeListState {
  return normalizeState(state)
}

export function parseStoredAnimeListState(
  raw: string | null,
): AnimeListState | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    const record = asRecord(parsed)
    if (
      !record.filters ||
      !record.sortBy ||
      parsePositiveInt(record.page) === undefined
    ) {
      return null
    }
    return normalizeState(parsed)
  } catch {
    return null
  }
}
